import { Descriptions } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { deleteNotice, getNotice } from "../../../api/homepageManagementHomeNotice.api";
import Button from "../../../components/Button";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from "../../../components/layout/SimpleHeader";
import Loader from "../../../components/Loader";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetNotice } from "../../../models/homepageManagementHomeNotice.model";
import { RootState } from "../../../store/rootReducer";

interface IMatch {
  campusCode: string;
  noticeCode: string;
}

const NoticeRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const campusCode = match.params.campusCode;
  const noticeCode = Number(match.params.noticeCode);
  const history = useHistory();
  const location = useLocation();

  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetNotice>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (campusCode && noticeCode) {
      execute(getNotice(campusCode, noticeCode));
    }
  }, [campusCode, noticeCode]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.delete'));
      history.push(`/homepage/notice${location.search}`);
    }
  }, [submitRes, submitIsLoading]);

  const getCampusName = (campusCode: string) => {
    const campus = campusData?.find((item: any) => item.campusCode === campusCode);
    return campus?.campusKorName || '';
  }

  const onDelete = () => {
    if (!campusCode || !noticeCode) return;

    submitExecute(deleteNotice(campusCode, noticeCode));
  }

  return (
    <div className="content scrollbar">
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.notice.title3')}
      />

      <div className='comm-table__area rowType'>
        <Descriptions
          bordered
          column={3}
          size="small"
          labelStyle={{ width: '16%' }}
          contentStyle={{ width: '16%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={3}>
            {res?.data?.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.writer')}>
            {res?.data?.regMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column8')}>
            {moment(res?.data?.regDate || '').format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column7')}>
            {res?.data?.readCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('event.education.column11')}>
            {getCampusName(res?.data?.campusCode || '')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.notice.column1')}>
            {res?.data?.openYn === 'Y' ? t('common.text.public') : t('common.text.private')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.notice.column2')}>
            {res?.data?.bulletinSeq === 1 ? t('homepage.notice.text1') : t('homepage.notice.text2')}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.contents')} span={3}>
            <div dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }} />
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column5')} span={3}>
            {
              res?.data?.thumbnailFile
                ? (
                  <a
                    className="download-link btn-text mr-1"
                    href={res?.data?.thumbnailFile.fileLocation}
                    download={res?.data?.thumbnailFile.originalName}
                  >
                    {res?.data?.thumbnailFile.originalName}
                  </a>
                )
                : '-'
            }
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.file')} span={3}>
            {
              res?.data?.noticeFile
                ? res?.data?.noticeFile.map(item => (
                  <a
                    className="download-link btn-text mr-1"
                    href={item.fileLocation}
                    download={item.originalName}
                  >
                    {item.originalName}
                  </a>
                ))
                : '-'
            }
          </Descriptions.Item>
        </Descriptions>

        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              text={t('common.button.list')}
              onClick={() => history.push(`/homepage/notice${location.search}`)}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic gray"
              text={t('common.button.delete')}
              onClick={() => setConfirmDeleteVisible(true)}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn-basic pink-line"
              text={t('common.button.edit')}
              onClick={() => history.push(`/homepage/notice/${campusCode}/${noticeCode}/edit${location.search}`)}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
  );
}

export default NoticeRead;
